import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useStaticQuery, graphql } from "gatsby";
import cx from "classnames";
import NewsletterSignupForm from "../Forms/NewsletterSignupForm";

import CloseIcon from "../../assets/svgs/menu-close-icon.svg";
import useLocalStorage from "../../hooks/useLocalStorage";
import useElementOnScreen from "../../hooks/useElementOnScreen";

import "./style.scss";

const SubscribeBanner = ({ scrollPos = 500, content }) => {
  const [scrollY, setScrollY] = useState(0);
  const [isHideBanner, setIsHideBanner] = useLocalStorage(
    "hideLearnSubscribeBanner",
    false
  );
  const [showModal, setShowModal] = useState(false);
  const closeModal = () => {
    setShowModal(false);
    setIsHideBanner(true);
  };

  const [containerRef, isFullBannerVisible] = useElementOnScreen({
    root: null,
    rootMargin: "0px",
    threshold: 0,
  });

  const setPageYOffset = () => setScrollY(window.pageYOffset);
  const shouldShowBanner = !isHideBanner && scrollY > scrollPos && !showModal;

  useEffect(() => {
    if (!isHideBanner && scrollY <= scrollPos) {
      window.addEventListener("scroll", setPageYOffset);
    }

    return () => window.removeEventListener("scroll", setPageYOffset);
  });

  return content ? (
    <>
      <SubscribeModal
        showModal={showModal}
        closeModal={closeModal}
        content={content}
      />
      <div
        id="subscribe-banner--sticky"
        className={cx({ show: shouldShowBanner && !isFullBannerVisible })}
      >
        <button
          className="button close-btn position-absolute text-white"
          style={{ right: "0.75rem" }}
          onClick={() => setIsHideBanner(true)}
        >
          <CloseIcon />
        </button>
        <div className="grid-container align-items-center justify-content-center pt-md-4 pb-3">
          <div
            className="d-none d-md-block grid-col-6 grid-col-lg-5 grid-offset-lg-2 content text-white ml-4"
            dangerouslySetInnerHTML={{ __html: content.pContent }}
          />
          <NewsletterSignupForm
            classNames="d-none d-md-flex flex-nowrap grid-col-6 grid-col-lg-5"
            buttonText={content.pButtonText}
          />

          <div
            className="d-block d-md-none grid-col-5 grid-col-sm-6 grid-offset-2 grid-offset-sm-2 content text-white"
            dangerouslySetInnerHTML={{ __html: content.pIntroHeading }}
          />
          <img
            className="d-block d-md-none grid-col-5 content position-relative"
            src={content.pIcon.localFile.publicURL}
            height={100}
            style={{ top: "0.75rem" }}
          />
          <button
            className="d-block d-md-none grid-col-10 grid-offset-2 button button-primary-lime button-rounded"
            onClick={() => setShowModal(true)}
          >
            {content.pButtonText}
          </button>
        </div>
      </div>
      <div ref={containerRef}>
        <FullBanner
          title={content.pHeading}
          description={content.pContent}
          buttonText={content.pButtonText}
          imgSrc={content.pIcon.localFile.publicURL}
        />
      </div>
    </>
  ) : null;
};

const SubscribeModal = ({ showModal, closeModal, content }) => {
  return (
    <div
      id="subscribe-modal"
      className={cx("modal fade d-block", { show: showModal })}
      role="dialog"
      aria-labelledby="subscribe-modal-label"
      aria-hidden="true"
      tabIndex="-1"
    >
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content">
          <button
            className="button close-btn position-absolute text-white"
            style={{ right: "0" }}
            onClick={closeModal}
          >
            <CloseIcon />
          </button>
          <div className="modal-body p-0">
            <FullBanner
              title={content.pHeading}
              description={content.pContent}
              buttonText={content.pButtonText}
              imgSrc={content.pIcon.localFile.publicURL}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export const FullBanner = (props) => {
  const { t } = useTranslation();

  const data = useStaticQuery(graphql`
    query {
      imageSubscribeToNewsletter: file(
        relativePath: { eq: "images/subscribe-to-newsletter.svg" }
      ) {
        publicURL
      }
    }
  `);

  const {
    title = t("zmt.newsletter.title"),
    description = t("zmt.newsletter.description"),
    buttonText = t("form.newsletterSignUpForm.btnSubscribeNew"),
    imgSrc = data.imageSubscribeToNewsletter.publicURL,
  } = props;

  return (
    <div id="subscribe-to-newsletter">
      <div className="subscribe-to-newsletter">
        <div className="subscribe-to-newsletter-inner container">
          <div className="row">
            <div className="col-12 col-lg-7 col-xl-6 text-white py-md-5 my-auto">
              <h2
                className="text-white text-center text-lg-left"
                dangerouslySetInnerHTML={{ __html: title }}
              />
              <div
                className="text-center text-lg-left"
                dangerouslySetInnerHTML={{ __html: description }}
              />
              <NewsletterSignupForm
                classNames="zixel-subscribe-form mt-5"
                buttonClassNames="button button-rounded button-primary-lime pl-md-4 pr-md-4 mb-5 mb-md-0 subscribe-form-right"
                inputClassNames="form-control mr-lg-3 subscribe-form-left"
                buttonText={buttonText}
              />
            </div>
            <div className="col-12 col-lg-5 col-xl-6 subscribe-image-holder">
              <img
                src={imgSrc}
                alt="Subscribe to News"
                className="img-fluid subscribe-image"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscribeBanner;
